import axios, { type AxiosRequestConfig } from "axios";

import { SERVICE_HOST } from "./auth/config";
import { getToken } from "./auth/TokenStorage";

/**
 * Uses axios - method, url, event listeners, etc. are passed into the options
 * param as an object.
 * @name ServiceWrapper
 * @static
 * @param {{}} options
 * @returns {Promise<any>}
 */
const axiosInstance = axios.create({
    baseURL: SERVICE_HOST,
    timeout: 90000, // one and a half minutes before giving up
    headers: { "Content-Type": "application/json", "Access-Control-Max-Age": 86400 },
    withCredentials: true
});

// Augment the AxiosRequestConfig interface to include our custom retry property.
// This informs TypeScript that `__retryCount` is a valid property on the config object.
declare module "axios" {
    interface AxiosRequestConfig {
        __retryCount?: number;
    }
}

/**
 * Extracts a user-friendly error message from an unknown error type.
 * This handles Axios errors, standard Error objects, and other types gracefully,
 * avoiding the `[object Object]` stringification issue.
 */
const getErrorMessage = (error: unknown): string => {
    if (axios.isAxiosError(error)) {
        if (error.response) {
            // Avoid destructuring `error.response.data` which is `any` to satisfy the linter.
            const status = error.response.status;
            const data = error.response.data as unknown;
            let dataString = "No response data";
            if (data != null) {
                if (typeof data === "string") {
                    dataString = data;
                } else {
                    dataString = JSON.stringify(data);
                }
            }
            return `Request failed with status ${status}: ${dataString}`;
        }
        if (error.request) {
            return "No response received from the server.";
        }
    }
    if (error instanceof Error) {
        return error.message;
    }
    return "An unknown error occurred.";
};

/**
 * Determines if a request method is idempotent (safe to retry)
 */
const isIdempotentMethod = (method: string | undefined): boolean => {
    return ["GET", "HEAD", "OPTIONS", "DELETE"].includes(method?.toUpperCase() || "");
};

axiosInstance.interceptors.request.use(
    (config) => {
        const token = getToken();
        if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error: unknown) => {
        return Promise.reject(new Error(getErrorMessage(error)));
    }
);

axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: unknown): Promise<unknown> => {
        // Use a type guard to ensure we have an AxiosError. If not, we can't retry.
        if (!axios.isAxiosError(error)) {
            throw new Error(getErrorMessage(error));
        }

        // Now TypeScript knows `error` is an AxiosError, so `error.config` is safe to access.
        const { config, response } = error;

        // An Axios error might not have a config (e.g., network error before request was fully formed).
        if (!config) {
            throw error;
        }

        const { method } = config;
        // Add a custom retry count if it doesn't exist
        config.__retryCount ??= 0;

        // Max number of retries
        const MAX_RETRIES = 3;

        // ONLY retry if the error is a 5xx AND the method is idempotent
        const shouldRetry =
            response && response.status >= 500 && config.__retryCount < MAX_RETRIES && isIdempotentMethod(method);

        if (shouldRetry) {
            config.__retryCount += 1;

            // Optional: wait before retrying (simple backoff)
            await ServiceWrapper.wait(500 * config.__retryCount);

            // Retry the request
            return axiosInstance(config);
        }

        // Otherwise, throw the error
        throw error;
    }
);

interface ServiceOptions {
    url: string;
    method: string;
    data?: unknown;
    headers?: Record<string, string>;
    params?: unknown;
    withCredentials?: boolean;
    onUploadProgress?: (progressEvent: ProgressEvent) => void;
    signal?: AbortSignal;
    [key: string]: unknown;
}

const ServiceWrapper = (options: ServiceOptions): Promise<{ data: unknown }> =>
    axiosInstance(options as AxiosRequestConfig);

/**
 * Do we have a workable status? If not, throw an error with the status text
 * @name checkStatus
 * @static
 * @param {{}} response
 * @return {{}} response
 * @throws {Error} error
 */
ServiceWrapper.checkStatus = (response: { status: number }): { status: number } => {
    // Success status is any 200s response
    if (response.status >= 200 && response.status < 300) {
        return response;
    }
    throw new Error(`HTTP ${response.status}`);
};

// Helper wait function
ServiceWrapper.wait = (ms: number): Promise<void> => {
    return new Promise<void>((resolve) => {
        setTimeout(resolve, ms);
    });
};

/**
 * Detect the type of error and return the appropriate message
 * @name errorHandler
 * @static
 * @param {{}|string|null} error
 * @return {string|*}
 */
ServiceWrapper.errorHandler = getErrorMessage;

/**
 * Convert response headers into an array
 * @name responseHeadersAsArray
 * @static
 * @param {{}} response
 * @return {[]} headers
 */
ServiceWrapper.responseHeadersAsArray = (response: unknown): unknown => {
    if (response && typeof response === "object" && "headers" in response) {
        return { ...(response as { headers: Record<string, unknown> }).headers };
    }
    return {};
};

/**
 * Perform CRUD operations with an API
 */
ServiceWrapper.serviceCall = async ({ options }: { options: ServiceOptions }): Promise<{ data: unknown }> => {
    // The previous request de-duplication logic (using pendingRequests) was incompatible
    // with the AbortController pattern. It caused new requests to adopt old promises,
    // leading to premature cancellations. Removing the cache ensures each call is
    // independent and correctly tied to its own AbortSignal.
    return ServiceWrapper(options);
};

export default ServiceWrapper;
export type { ServiceOptions };
